import { Fragment, forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import DotsVerticalIcon from '@/public/icons/approval/dots-vertical.svg';
import ProfilesIcon from '@/public/icons/approval/profiles.svg';
import {
  HistoryListItem,
  CardAvatarMore,
  CardChipNo,
  CardGroupSep,
  CardAvatarStack,
  CardDate,
  CardMetaRow,
  CardPersonChip,
  CardPersonCoopMark,
  CardPersonDeactivated,
  CardPersonName,
  CardSummaryText,
  CardTitle,
  CardTitleRow,
  HistoryListItemFooterKebab,
} from './styles';
import type {
  RecentApprovalLineApprover,
  RecentApprovalLineSummary,
} from './RecentApprovalLineList';

type RecentApprovalLineCardProps = {
  item: RecentApprovalLineSummary;
  /** 외부에서 popover/drawer가 떠 있는 동안 hover 상태를 유지. */
  active?: boolean;
  /** 모바일 케밥 노출 여부. */
  showKebab?: boolean;
  /** 풋터 날짜 노출 여부. '나의 결재라인'은 등록일 미노출 */
  showDate?: boolean;
  /** 모바일 케밥 클릭 */
  onKebabClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  /** 카드 전체 클릭 — 결재라인에 적용. 케밥은 stopPropagation으로 분리 */
  onCardClick?: () => void;
};

const RecentApprovalLineCard = forwardRef<
  HTMLDivElement,
  RecentApprovalLineCardProps
>(
  (
    {
      item,
      active = false,
      showKebab = false,
      showDate = true,
      onKebabClick,
      onCardClick,
    },
    ref,
  ) => {
    const { t } = useTranslation();
    const cooperators = item.cooperators ?? [];

    const cardClickable = Boolean(onCardClick);

    // 대안 뷰용 — 카드가 스스로 구성을 말하게 한다
    const referrerCount = item.referrers?.length ?? 0;
    // 0 이어도 자리를 비우지 않는다 — 항목이 있다 없다 하면 오히려 읽기 어렵다.
    // 퇴사자는 예외 상황이라 있을 때만 덧붙인다 (접혀서 칩이 안 보여도 존재는 드러나야 함).
    const deactivatedCount = [...item.approvers, ...cooperators].filter(
      (p) => p.deactivated,
    ).length;
    const summaryText = [
      t('결재 {{n}}', { n: item.approvers.length }),
      t('협조 {{n}}', { n: cooperators.length }),
      t('참조 {{n}}', { n: referrerCount }),
      deactivatedCount > 0 ? t('퇴사 {{n}}', { n: deactivatedCount }) : null,
    ]
      .filter(Boolean)
      .join(' · ');
    // 협조가 끼는 자리와 단계 번호를 사이드바 결재라인과 같은 규칙으로 계산한다.
    // 협조 묶음도 한 단계를 차지하므로, 순서대로 처리면 뒤 결재자 번호가 그만큼 밀린다.
    const facePool = (() => {
      const out: (Partial<RecentApprovalLineApprover> & {
        key: string;
        isCoop: boolean;
        no?: number;
        /** 'group' 안 — 협조자 전원을 칩 하나로 묶는다 */
        groupItems?: { name: string; no?: number; deactivated?: boolean }[];
        /** 동시 처리일 때 묶음 전체가 쓰는 단계 번호 */
        groupNo?: number;
      })[] = [];
      const insertAt = item.cooperationInsertIndex ?? item.approvers.length;
      let step = 0;
      const pushCoop = () => {
        if (cooperators.length === 0) return;
        {
          out.push({
            key: 'coop-group',
            isCoop: true,
            // 순서대로면 이름마다 번호, 동시면 묶음 앞에 번호 하나
            groupItems: cooperators.map((c, ci) => ({
              name: c.name,
              no: item.cooperationOrdered ? step + ci + 1 : undefined,
              deactivated: c.deactivated,
            })),
            groupNo: item.cooperationOrdered ? undefined : step + 1,
          });
        }
        step += item.cooperationOrdered ? cooperators.length : 1;
      };
      item.approvers.forEach((a, i) => {
        if (i === insertAt) pushCoop();
        step += 1;
        out.push({ ...a, key: a.passportId, isCoop: false, no: step });
      });
      if (insertAt >= item.approvers.length) pushCoop();
      return out;
    })();
    // 줄바꿈을 허용하므로 넉넉히 — 너무 많이 접히면 어느 라인인지 알아보기 어렵다
    const FACE_MAX = 6;

    const facePeople = facePool.slice(0, FACE_MAX);
    // 협조 묶음 칩 하나가 여러 명을 담으므로 잘린 인원은 칩 수가 아니라 사람 수로 센다
    const headCount = (chips: { groupItems?: { name: string }[] }[]): number =>
      chips.reduce((n, c) => n + (c.groupItems ? c.groupItems.length : 1), 0);
    const faceOverflow = headCount(facePool) - headCount(facePeople);

    return (
      <HistoryListItem
        ref={ref}
        active={active}
        clickable={cardClickable}
        role={cardClickable ? 'button' : undefined}
        tabIndex={cardClickable ? 0 : undefined}
        aria-label={
          cardClickable
            ? t('{{title}} 결재라인 적용', { title: item.title })
            : undefined
        }
        onClick={cardClickable ? onCardClick : undefined}
        onKeyDown={
          cardClickable
            ? (e: React.KeyboardEvent<HTMLDivElement>) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onCardClick?.();
                }
              }
            : undefined
        }
      >
        <>
          <CardTitleRow>
            <CardTitle title={item.title}>{item.title}</CardTitle>
            {showDate && <CardDate>{formatDate(item.lastUsedAt)}</CardDate>}
          </CardTitleRow>
          <CardMetaRow>
            {
              <CardAvatarStack inlineFlow>
                {facePeople.map((p) => {
                  const group = p.groupItems;
                  const label = group
                    ? group.map((g) => g.name).join(' · ')
                    : p.name ?? '';
                  return (
                    <CardPersonChip
                      key={p.key}
                      tone={p.isCoop ? 'coop' : 'approver'}
                      wide={Boolean(group)}
                      title={
                        p.isCoop
                          ? t('{{name}} (협조)', { name: label })
                          : p.deactivated
                          ? `${label} (${t('퇴사')})`
                          : label
                      }
                    >
                      {p.isCoop && (
                        <CardPersonCoopMark
                          aria-label={t('협조', { context: 'role' })}
                        >
                          <ProfilesIcon width={14} height={14} />
                        </CardPersonCoopMark>
                      )}
                      {group ? (
                        <>
                          {p.groupNo != null && (
                            <CardChipNo className="chip-no" aria-hidden>
                              {p.groupNo}
                            </CardChipNo>
                          )}
                          {group.map((g, gi) => (
                            <Fragment key={g.name + gi}>
                              {g.no != null ? (
                                <CardChipNo className="chip-no" aria-hidden>
                                  {g.no}
                                </CardChipNo>
                              ) : (
                                gi > 0 && (
                                  <CardGroupSep
                                    className="chip-sep"
                                    aria-hidden
                                  >
                                    ·
                                  </CardGroupSep>
                                )
                              )}
                              {g.deactivated && (
                                <CardPersonDeactivated className="chip-out">
                                  {t('퇴사')}
                                </CardPersonDeactivated>
                              )}
                              <CardPersonName className="chip-nm">
                                {g.name}
                              </CardPersonName>
                            </Fragment>
                          ))}
                        </>
                      ) : (
                        <>
                          {p.no != null && (
                            <CardChipNo aria-hidden>{p.no}</CardChipNo>
                          )}
                          {p.deactivated && (
                            <CardPersonDeactivated>
                              {t('퇴사')}
                            </CardPersonDeactivated>
                          )}
                          <CardPersonName>{label}</CardPersonName>
                        </>
                      )}
                    </CardPersonChip>
                  );
                })}
                {faceOverflow > 0 && (
                  <CardAvatarMore>
                    {t('외 {{n}}명', { n: faceOverflow })}
                  </CardAvatarMore>
                )}
              </CardAvatarStack>
            }
            <CardSummaryText>{summaryText}</CardSummaryText>
          </CardMetaRow>
          {showKebab && (
            <HistoryListItemFooterKebab
              type="button"
              aria-label={t('{{title}} 결재라인 자세히 보기', {
                title: item.title,
              })}
              onClick={(e) => {
                e.stopPropagation();
                onKebabClick?.(e);
              }}
            >
              <DotsVerticalIcon width={16} height={16} />
            </HistoryListItemFooterKebab>
          )}
        </>
      </HistoryListItem>
    );
  },
);

RecentApprovalLineCard.displayName = 'RecentApprovalLineCard';

const formatDate = (iso: string) => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

export default RecentApprovalLineCard;
